import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTodayMetrics } from '../../services/api';

const AdminNavbar = ({ user, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const getGreeting = () => {
    const hr = new Date().getHours();
    if (hr < 12) return 'Good Morning';
    if (hr < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const [metrics, setMetrics] = React.useState({ todays_sales: 0, points_today: 0 });

  React.useEffect(() => {
    if (mobileMenuOpen) {
      getTodayMetrics().then(res => setMetrics(res.data)).catch(() => {});
    }
  }, [mobileMenuOpen]);

  const handleGuideClick = () => {
    // Open the user guide in a new tab
    window.open('/user-guide.html', '_blank');
  };

  const handleAdminLogin = () => {
    // Redirect to admin login
    window.location.href = '/login';
  };

  // Only show employee-specific menu items for non-admin users
  const isEmployee = user && user.role === 'employee';

  return (
    <nav className="bg-white shadow">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => navigate(user?.role === 'admin' ? '/admin' : '/employee')}>
              {/* Custom Fuel Pump Logo with Background */}
              <div className="relative h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-gradient-to-br from-orange-400 via-red-500 to-red-600 shadow-md flex items-center justify-center">
                <svg className="h-6 w-6 sm:h-7 sm:w-7 text-white" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 2h10c.55 0 1 .45 1 1v15c0 .55-.45 1-1 1H3c-.55 0-1-.45-1-1V3c0-.55.45-1 1-1zm1 2v5h8V4H4zm0 7v6h8v-6H4z" fill="#FFF"/>
                  <path d="M18 7.17L16.83 6 15 7.83 13.17 6 12 7.17l2 2v8.66c0 1.2.8 2.17 1.67 2.17h.66c.87 0 1.67-.97 1.67-2.17V9.17l2-2zM16.5 17h-1v-6h1v6z" fill="#FFD700"/>
                  <circle cx="8" cy="15.5" r="1.5" fill="#90EE90"/>
                </svg>
              </div>
              <span className="ml-2 sm:ml-3 text-sm sm:text-base md:text-xl font-bold text-gray-900 hover:text-primary-600 transition-colors">Employee Manager</span>
            </div>
            <div className="hidden lg:ml-6 lg:flex lg:space-x-8">
              {isEmployee ? (
                <>
                  <button
                    onClick={() => navigate('/employee')}
                    className={`${location.pathname === '/employee' ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium whitespace-nowrap`}
                  >
                    My Records
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/admin')}
                    className={`${location.pathname === '/' || location.pathname === '/admin' ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium whitespace-nowrap`}
                  >
                    Dashboard
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="hidden lg:ml-6 lg:flex lg:items-center">
            <div className="flex items-center space-x-2 xl:space-x-3">
              <div className="text-xs sm:text-sm text-gray-700">
                <span className="font-medium truncate max-w-[100px] xl:max-w-none inline-block">{user?.name}</span>
                <span className="ml-1 xl:ml-2 px-1.5 xl:px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 text-primary-800 capitalize">
                  {user?.role}
                </span>
              </div>
              <button
                onClick={onLogout}
                className="ml-1 xl:ml-2 inline-flex items-center px-3 xl:px-4 py-2 border border-transparent text-xs xl:text-sm leading-4 font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 whitespace-nowrap"
              >
                Logout
              </button>
            </div>
          </div>
          <div className="-mr-2 flex items-center lg:hidden">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu — full screen panel for mobile UX */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-gradient-to-b from-primary-700 to-secondary-800 text-white">
          <div className="max-w-3xl mx-auto px-4 pt-6 pb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {/* Custom Fuel Pump Logo with Background */}
                <div className="relative h-12 w-12 rounded-lg bg-gradient-to-br from-orange-300 via-red-400 to-red-500 shadow-lg flex items-center justify-center">
                  <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 2h10c.55 0 1 .45 1 1v15c0 .55-.45 1-1 1H3c-.55 0-1-.45-1-1V3c0-.55.45-1 1-1zm1 2v5h8V4H4zm0 7v6h8v-6H4z" fill="#FFF"/>
                    <path d="M18 7.17L16.83 6 15 7.83 13.17 6 12 7.17l2 2v8.66c0 1.2.8 2.17 1.67 2.17h.66c.87 0 1.67-.97 1.67-2.17V9.17l2-2zM16.5 17h-1v-6h1v6z" fill="#FFD700"/>
                    <circle cx="8" cy="15.5" r="1.5" fill="#90EE90"/>
                  </svg>
                </div>
                <div className="ml-3">
                  <div className="text-lg font-semibold">Employee Manager</div>
                  <div className="text-sm opacity-90">{getGreeting()}, {user?.name}</div>
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-md bg-white bg-opacity-10">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Quick stats - only show for employees */}
            {isEmployee && (
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                  <div className="text-xs opacity-90">Today's Sales</div>
                  <div className="mt-1 font-semibold">₹{(metrics.todays_sales || 0).toFixed ? metrics.todays_sales.toFixed(2) : metrics.todays_sales}</div>
                </div>
                <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                  <div className="text-xs opacity-90">Points</div>
                  <div className="mt-1 font-semibold">{metrics.points_today ?? user?.points ?? 0}</div>
                </div>
              </div>
            )}

            {/* Action buttons - only show for employees */}
            {isEmployee && (
              <div className="mt-6 grid grid-cols-2 gap-4">
                <button onClick={() => { navigate('/employee'); setMobileMenuOpen(false); }} className="bg-white bg-opacity-10 rounded-lg py-3 flex flex-col items-center">
                  <div className="text-sm font-medium">Sales Entry</div>
                </button>
                <button onClick={() => { navigate('/employee/leaderboard'); setMobileMenuOpen(false); }} className="bg-white bg-opacity-10 rounded-lg py-3 flex flex-col items-center">
                  <div className="text-sm font-medium">Leaderboard</div>
                </button>
                <button onClick={() => { navigate('/employee/contests'); setMobileMenuOpen(false); }} className="bg-white bg-opacity-10 rounded-lg py-3 flex flex-col items-center">
                  <div className="text-sm font-medium">Contests</div>
                </button>
                <button onClick={() => { navigate('/employee/rewards'); setMobileMenuOpen(false); }} className="bg-white bg-opacity-10 rounded-lg py-3 flex flex-col items-center">
                  <div className="text-sm font-medium">Rewards</div>
                </button>
              </div>
            )}

            <div className="mt-8 border-t border-white border-opacity-10 pt-4">
              <button onClick={() => { handleGuideClick(); setMobileMenuOpen(false); }} className="w-full text-left py-3 text-sm">User Guide</button>
              <button onClick={() => { onLogout(); setMobileMenuOpen(false); }} className="w-full text-left py-3 text-sm">Sign out</button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default AdminNavbar;